import { useState } from "react";
import { Button, Select, Text, tokens } from "@fluentui/react-components";
import { SendRegular, CopyRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { formatTextToHTML } from "../utils/format";
import LoadingDots from "./LoadingDots";

export default function TranslationCard({
  translateLang,
  setTranslateLang,
  translatedText,
  isTranslating,
  isError,
  isEmpty,
  onUseAsMail,
  t,
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    const text = translatedText || "";
    const ok = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(ok).catch(() => fallbackCopy(text, ok));
    } else {
      fallbackCopy(text, ok);
    }
  }

  const showFooter = !isTranslating && !isError && !isEmpty && !!translatedText;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        flex: 1,
        minHeight: 0,
        padding: "2px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <Select
          size="small"
          value={translateLang}
          onChange={(_, d) => setTranslateLang(d.value)}
          aria-label={t("translate.target")}
          style={{ marginLeft: "auto", minWidth: "120px" }}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
          <option value="de">Deutsch</option>
        </Select>
      </div>

      <div
        style={{
          fontSize: "13px",
          lineHeight: "1.4",
          color: tokens.colorNeutralForeground1,
          wordBreak: "break-word",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {isTranslating && !translatedText && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LoadingDots size="small" />
            <Text size={200} style={{ color: tokens.colorNeutralForeground2, fontStyle: "italic" }}>
              {t("status.loading")}
            </Text>
          </div>
        )}

        {!isTranslating && isError && (
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{t("result.error")}</Text>
        )}

        {!isTranslating && !isError && isEmpty && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {t("translate.empty")}
          </Text>
        )}

        {!isError && !isEmpty && translatedText && (
          <div dangerouslySetInnerHTML={{ __html: formatTextToHTML(translatedText) }} />
        )}
      </div>

      {showFooter && (
        <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
          <Button
            icon={<SendRegular />}
            size="small"
            onClick={() => onUseAsMail(translatedText)}
            style={{
              backgroundColor: "var(--aco-accent-bg)",
              color: "var(--aco-accent)",
              border: "1px solid var(--aco-accent-border)",
              borderRadius: "4px",
              height: "26px",
              fontSize: "11px",
            }}
          >
            {t("btn.use_as_mail")}
          </Button>
          <Button
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            appearance="subtle"
            size="small"
            onClick={handleCopy}
            title={t("btn.copy")}
          />
        </div>
      )}
    </div>
  );
}

function fallbackCopy(text, onSuccess) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;opacity:0;top:0;left:0;";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    if (document.execCommand("copy")) onSuccess();
    document.body.removeChild(ta);
  } catch {
    // ignore
  }
}
